import React from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';

const UPDATED = 'February 2026';

export default function Terms() {
  return (
    <div className="bg-[#0a0a0b] text-zinc-100 min-h-screen">
      <SEO
        title="Terms of Service — Reachly"
        description="The rules for using Reachly's email-outreach service — acceptable use, billing, cancellation, and liability."
      />
      <section className="max-w-3xl mx-auto px-5 lg:px-8 pt-16 pb-24">
        <div className="text-sm uppercase tracking-widest text-[#d4a94a]">Legal</div>
        <h1 className="mt-3 font-display text-5xl md:text-6xl">Terms of Service</h1>
        <div className="mt-3 text-sm text-zinc-500">Last updated: {UPDATED}</div>

        <article className="prose prose-invert prose-zinc max-w-none mt-10 prose-headings:font-display prose-a:text-[#f5d67a]">
          <p>
            These Terms of Service ("Terms") govern your access to and use of
            Reachly ("we", "us", or "the Service") at ireachly.com. By creating
            an account or using the Service, you agree to these Terms.
          </p>

          <h2>1. Your account</h2>
          <p>
            You must be at least 16 years old and provide accurate information
            when creating an account. You're responsible for keeping your
            credentials secret and for all activity under your account. Notify
            us at <a href="mailto:hello@ireachly.com">hello@ireachly.com</a> if
            you suspect any unauthorized use.
          </p>

          <h2>2. Acceptable use</h2>
          <p>You agree not to use Reachly to:</p>
          <ul>
            <li>Send unsolicited commercial email in violation of CAN-SPAM, CASL, GDPR, PECR, or any other applicable law.</li>
            <li>Send emails to purchased, scraped, or otherwise unauthorised recipient lists.</li>
            <li>Impersonate any person or entity, or misrepresent your affiliation with any person or entity.</li>
            <li>Distribute malware, phishing content, or content that violates intellectual-property rights.</li>
            <li>Send emails to any recipient after they have unsubscribed or replied "stop".</li>
            <li>Circumvent Reachly's rate limits, warm-up, or reply-detection features.</li>
          </ul>
          <p>
            Every campaign you send must include an accurate sender identity
            and an unsubscribe mechanism if required by law in the recipient's
            jurisdiction. You are solely responsible for the content of the
            emails you send and for compliance with applicable law.
          </p>

          <h2>3. Your data & your customers' data</h2>
          <p>
            You retain ownership of the content you create in Reachly, including
            your contact lists, campaigns, and templates. You grant Reachly a
            worldwide, non-exclusive licence to host, process, and transmit that
            content solely to provide the Service to you. See our{' '}
            <Link to="/privacy">Privacy Policy</Link> for how we handle personal data.
          </p>

          <h2>4. Third-party accounts</h2>
          <p>
            Reachly integrates with Google (Gmail) and Microsoft (Outlook) via
            their respective OAuth flows. You are subject to those providers'
            terms in addition to these Terms. You may disconnect your Google or
            Microsoft account at any time from Reachly's settings — this
            immediately removes our access.
          </p>

          <h2>5. Plans, billing & cancellation</h2>
          <ul>
            <li>Reachly offers a Free tier and paid tiers. Paid subscriptions are billed monthly or annually and renew automatically until cancelled.</li>
            <li>You can cancel from Settings at any time. Cancellations take effect at the end of the current billing period; we do not refund partial periods except where required by law.</li>
            <li>Promotional codes (e.g. `JJoyUnlim1`) are non-transferable and can be revoked at our discretion in cases of abuse.</li>
            <li>All fees are exclusive of applicable taxes, which you are responsible for paying.</li>
          </ul>

          <h2>6. Suspension & termination</h2>
          <p>
            We may suspend or terminate your account with notice if you violate
            these Terms, misuse the Service, or fail to pay applicable fees.
            Serious violations (spam, illegal content, security abuse) may
            result in immediate termination without notice.
          </p>

          <h2>7. Warranties & disclaimers</h2>
          <p>
            The Service is provided <strong>"as is"</strong> and{' '}
            <strong>"as available"</strong> without warranties of any kind. We
            do not guarantee that emails will be delivered or that the Service
            will be uninterrupted or error-free. Deliverability depends on
            factors outside our control, including your sender reputation, the
            recipient's mail server, and the content of your emails.
          </p>

          <h2>8. Limitation of liability</h2>
          <p>
            To the maximum extent permitted by law, in no event will Reachly be
            liable for any indirect, incidental, special, consequential, or
            punitive damages, or any loss of profits or revenue, arising out of
            or relating to the Service. Our total liability for any claim
            arising out of or relating to these Terms will not exceed the fees
            you paid us in the 12 months preceding the claim, or USD 100,
            whichever is greater.
          </p>

          <h2>9. Indemnification</h2>
          <p>
            You agree to defend, indemnify and hold Reachly harmless from any
            claim arising out of your use of the Service, your content, or your
            violation of these Terms — including claims from your email
            recipients or their mail providers.
          </p>

          <h2>10. Changes</h2>
          <p>
            We may update these Terms from time to time. Material changes will
            be notified by email or in-app at least 14 days before they take
            effect. Continuing to use the Service after the effective date
            constitutes acceptance of the new Terms.
          </p>

          <h2>11. Governing law</h2>
          <p>
            These Terms are governed by the laws of the State of Delaware, USA,
            without regard to conflict-of-laws principles. Any dispute will be
            resolved in the state or federal courts located in Delaware.
          </p>

          <h2>12. Contact</h2>
          <p>
            Reachly · <a href="mailto:hello@ireachly.com">hello@ireachly.com</a>
          </p>
        </article>
      </section>
    </div>
  );
}
